/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { BasicCard } from '@coursera-web/cds-labs';

import { getLanguageCode } from 'js/lib/language';

import { Typography, breakpoints } from '@coursera/cds-core';
import type { Button } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import { getSlugFromHref } from 'bundles/premium-hub/utils';

type Props = {
  title: string;
  slug?: string;
  callToActionLink: string;
  id?: string;
};

const styles = {
  categoryCard: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    padding: var(--cds-spacing-150);
    display: flex;
    align-items: center;
    height: 100%;

    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-400);
      border: 0;
    }
  `,

  title: css`
    ${breakpoints.down('sm')} {
      font-size: var(--cds-font-size-175);
      font-weight: var(--cds-font-weight-600);
    }
  `,
};

/* This is for a11y purposes, it allows SRs to read in Spanish.
  This is not a robust solution, but the degrees surface area is unlikely to change in a major way very often.
*/
const LATIN_AMERICA_ID = 'TOP_LATAM';

const CategoryCard = ({ slug, callToActionLink, title, id }: Props) => {
  const trackV3 = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const finalSlug = slug || getSlugFromHref(callToActionLink);

  /**
   *  Accessibility specific functionality for spanish text on english users.
   *  This is a specific copy decision for english users, and other langauges have
   *  natural translations for this copy.
   */
  const userLanguageIsEnglish = getLanguageCode() === 'en';
  const langProps: { lang?: string } = {};
  if (userLanguageIsEnglish && id === LATIN_AMERICA_ID) {
    langProps.lang = 'es';
  }

  return (
    <BasicCard
      component={TrackedLink2}
      css={styles.categoryCard}
      href={callToActionLink}
      aria-label={`${title}`}
      data-e2e={`${finalSlug}-category-card`}
      trackingName="category_link"
      withVisibilityTracking={true}
      requireFullyVisible={true}
      data={{
        entityId: finalSlug,
        link: callToActionLink,
        title,
      }}
      onClick={() =>
        trackV3('click_button', {
          button: {
            name: 'degrees_category_hub',
            linkType: 'internal',
            linkName: title,
            linkURL: callToActionLink,
          } as Button,
          pageSection: {
            sectionName: 'browse_by',
            index: sectionIndex,
          },
        })
      }
    >
      <div>
        <Typography variant="h2semibold" component="p" css={styles.title} {...langProps}>
          {title}
        </Typography>
      </div>
    </BasicCard>
  );
};

export default CategoryCard;
